import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { WhatsappLogo, CheckCircle, ShieldStar, Warning } from "@phosphor-icons/react";

export default function ActivatePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/activate/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setError("This activation link is invalid or expired. Contact ResQconnect dispatch."));
  }, [token]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#09090b] text-white p-4" data-testid="activate-page">
      <div className="w-full max-w-md border border-zinc-800 bg-[#121214] p-8">
        <div className="flex items-center gap-2 mb-6">
          <ShieldStar size={26} weight="duotone" className="text-[#007AFF]" />
          <div className="font-heading font-black tracking-tighter text-lg">RESQCONNECT</div>
        </div>

        {error && !info && <div className="text-red-400 text-sm" data-testid="activate-error">{error}</div>}

        {info && !info.twilio_configured && (
          <div className="border border-yellow-500/30 bg-yellow-500/10 p-4 flex gap-3" data-testid="activate-not-configured">
            <Warning size={22} weight="duotone" className="text-yellow-400 shrink-0" />
            <div>
              <div className="font-bold text-yellow-400">WhatsApp alerts not configured yet</div>
              <p className="text-xs text-zinc-400 mt-1">Ask ResQconnect dispatch to complete Twilio setup &mdash; you will receive a fresh activation link once it is live. Email dispatch still works.</p>
            </div>
          </div>
        )}

        {info && info.twilio_configured && (
          <>
            <h1 className="font-heading text-2xl font-black tracking-tighter">Hi {info.name} — one tap to activate</h1>
            {info.already_active && (
              <div className="text-green-400 text-xs border border-green-500/30 bg-green-500/10 p-2 mt-3 flex items-center gap-2" data-testid="activate-already">
                <CheckCircle size={16} weight="duotone" /> Your WhatsApp is already connected. You&apos;re set.
              </div>
            )}
            <p className="text-zinc-400 text-sm mt-4">
              Save <span className="font-mono-data text-white font-bold">{info.resq_wa_number}</span> as a contact (name it <i>ResQconnect Dispatch</i>).
              Then tap the button — WhatsApp opens with the word <b>ACTIVATE</b> already typed. Send it.
            </p>

            <a href={info.wa_link} target="_blank" rel="noopener noreferrer"
              className="btn-tactical bg-[#25D366] hover:bg-[#1eb257] text-black flex items-center justify-center gap-2 w-full mt-6 no-underline"
              data-testid="activate-whatsapp-button">
              <WhatsappLogo size={20} weight="bold" /> OPEN WHATSAPP &amp; SEND ACTIVATE
            </a>

            <div className="mt-6 flex items-center gap-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(info.wa_link)}`}
                alt="Scan to activate WhatsApp"
                className="w-28 h-28 bg-white p-1"
                data-testid="activate-qr-code"
              />
              <p className="text-zinc-500 text-xs">On a computer? Scan this QR with your phone camera — same one-tap activation.</p>
            </div>

            <p className="text-zinc-500 text-xs mt-6 border-t border-zinc-800 pt-3">
              You will receive an instant confirmation on WhatsApp. Every ResQconnect dispatch will be pushed to you automatically &mdash; no keys, no shared bot.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
